import React, { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { CalendarIcon, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "./primitives";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { HOTEL } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EnquiryForm = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: "", message: "" });
  const [enquiryType, setEnquiryType] = useState("room");
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        enquiry_type: enquiryType,
        check_in: checkIn ? format(checkIn, "yyyy-MM-dd") : null,
        check_out: checkOut ? format(checkOut, "yyyy-MM-dd") : null,
        guests: form.guests ? parseInt(form.guests, 10) : null,
        message: form.message || null,
      });
      toast.success("Enquiry sent! Our team will contact you shortly.");
      setForm({ name: "", email: "", phone: "", guests: "", message: "" });
      setCheckIn(null); setCheckOut(null); setEnquiryType("room");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="enquiry" className="bg-[#0D120E] text-[#F5F2ED] py-24 md:py-40">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Reveal><span className="overline text-[#B89965]">Reserve Your Stay</span></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95] mt-6">Let&rsquo;s plan<br /><span className="italic text-[#B89965]">your visit.</span></h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 text-[#F5F2ED]/70 leading-relaxed max-w-sm">Send us an enquiry and our front desk will confirm availability and the best direct rate. Or reach us right away:</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 space-y-2 font-display text-2xl">
              <a href={`tel:${HOTEL.phone}`} className="block hover:text-[#B89965] transition-colors" data-testid="enquiry-phone">{HOTEL.phoneDisplay}</a>
              <a href={`mailto:${HOTEL.email}`} className="block text-xl text-[#F5F2ED]/70 hover:text-[#B89965] transition-colors" data-testid="enquiry-email">{HOTEL.email}</a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="md:col-span-7">
          <form onSubmit={submit} data-testid="enquiry-form" className="grid sm:grid-cols-2 gap-x-6 gap-y-7">
            <div className="sm:col-span-2">
              <Label className="overline text-[#F5F2ED]/60 text-[10px]">I&rsquo;m enquiring about</Label>
              <Select value={enquiryType} onValueChange={setEnquiryType}>
                <SelectTrigger data-testid="enquiry-type" className="mt-2 bg-transparent border-0 border-b border-[#F5F2ED]/25 rounded-none px-0 text-[#F5F2ED] focus:ring-0 focus:border-[#B89965]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="room">Room Booking</SelectItem>
                  <SelectItem value="banquet">Banquet / Event</SelectItem>
                  <SelectItem value="restaurant">ROOFTOPz Reservation</SelectItem>
                  <SelectItem value="general">General Enquiry</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <FieldInput label="Full Name" value={form.name} onChange={set("name")} testid="input-name" />
            <FieldInput label="Phone" value={form.phone} onChange={set("phone")} testid="input-phone" />
            <FieldInput label="Email" type="email" value={form.email} onChange={set("email")} testid="input-email" />
            <FieldInput label="Guests" type="number" value={form.guests} onChange={set("guests")} testid="input-guests" />

            <FieldDate label="Check-in" value={checkIn} onChange={setCheckIn} testid="input-checkin" />
            <FieldDate label="Check-out" value={checkOut} onChange={setCheckOut} testid="input-checkout" />

            <div className="sm:col-span-2">
              <Label className="overline text-[#F5F2ED]/60 text-[10px]">Message (optional)</Label>
              <Textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={3} className="mt-2 bg-transparent border-0 border-b border-[#F5F2ED]/25 rounded-none px-0 text-[#F5F2ED] resize-none focus-visible:ring-0 focus-visible:border-[#B89965]" placeholder="Tell us anything that helps us prepare for your stay…" />
            </div>

            <div className="sm:col-span-2">
              <button type="submit" disabled={loading} data-testid="enquiry-submit" className="btn-fill w-full sm:w-auto inline-flex items-center justify-center gap-2 border border-[#B89965] text-[#B89965] hover:text-[#0D120E] rounded-full px-10 py-4 disabled:opacity-60">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Enquiry <ArrowUpRight size={16} /></>}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
};

const FieldDate = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="overline text-[#F5F2ED]/60 text-[10px]">{label}</Label>
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid={testid} className="mt-2 w-full flex items-center justify-between bg-transparent border-b border-[#F5F2ED]/25 pb-2.5 text-left text-[#F5F2ED] hover:border-[#B89965] transition-colors">
          <span className={value ? "" : "text-[#F5F2ED]/40"}>{value ? format(value, "dd MMM yyyy") : "Select date"}</span>
          <CalendarIcon size={16} className="text-[#B89965]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
      </PopoverContent>
    </Popover>
  </div>
);

const FieldInput = ({ label, testid, type = "text", ...rest }) => (
  <div>
    <Label className="overline text-[#F5F2ED]/60 text-[10px]">{label}</Label>
    <Input type={type} data-testid={testid} {...rest} className="mt-2 bg-transparent border-0 border-b border-[#F5F2ED]/25 rounded-none px-0 text-[#F5F2ED] focus-visible:ring-0 focus-visible:border-[#B89965]" />
  </div>
);
